import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Home, Tv, Users, Rss, HelpCircle, LogOut, User, Shield, Share2, Info, Bell, MapPin, Sparkles, Lock, Mail } from 'lucide-react';
import { HayRollIcon, RibbonIcon, FenceIcon, SheepIcon } from './FlockIcons';
import ShareModal from './ShareModal';
import NotificationsDropdown from './NotificationsDropdown';
import { useTour } from '../contexts/TourContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = ({ user, setUser, currentPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { startTour } = useTour();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Safety check - if no user, return null (should not happen, but prevents crashes)
  if (!user || !user.name) {
    return null;
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    checkAdminStatus();
    loadUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        withCredentials: true
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleNotificationsToggle = () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      // Reload count when opening
      loadUnreadCount();
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear localStorage backup token for iOS Safari
      localStorage.removeItem('backup_session_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      // Still clear local storage even if logout API fails
      localStorage.removeItem('backup_session_token');
    }
  };

  // Desktop nav items (full list)
  const desktopNavItems = [
    { id: 'my-shows', label: 'My BaadTV', icon: 'ribbon', path: '/my-shows' },
    { id: 'fresh-pastures', label: 'Graze', icon: Sparkles, path: '/fresh-pastures' },
    { id: 'feed', label: 'My Feed', icon: 'hay-roll', path: '/feed' },
    { id: 'friends', label: 'The Flock', icon: 'sheep', path: '/friends' },
    { id: 'help', label: 'Help', icon: HelpCircle, path: '/help' },
    { id: 'about', label: 'About', icon: Info, path: '/about' },
  ];

  // Admin is accessible via the profile dropdown — no header link needed.

  // Mobile bottom nav items (5 main pages including Fresh Pastures)
  const mobileBottomNavItems = [
    { id: 'dashboard', label: 'Home', icon: 'fence', path: '/dashboard' },
    { id: 'my-shows', label: 'My BaadTV', icon: 'ribbon', path: '/my-shows' },
    { id: 'fresh-pastures', label: 'Graze', icon: Sparkles, path: '/fresh-pastures' },
    { id: 'feed', label: 'Feed', icon: 'hay-roll', path: '/feed' },
    { id: 'friends', label: 'Flock', icon: 'sheep', path: '/friends' },
  ];

  const renderIcon = (icon, isActive) => {
    const iconColor = isActive ? '#E62F88' : '#b0b0b0';
    const size = isMobile ? 24 : 18;
    
    if (icon === 'fence') {
      return <FenceIcon size={size} color={iconColor} className="nav-icon" />;
    } else if (icon === 'ribbon') {
      return <RibbonIcon size={size} color={iconColor} className="nav-icon" />;
    } else if (icon === 'hay-roll') {
      return <HayRollIcon size={size} color={iconColor} className="nav-icon" />;
    } else if (icon === 'sheep') {
      return <SheepIcon size={size} color={iconColor} className="nav-icon" />;
    } else {
      return React.createElement(icon, { size, color: iconColor, className: 'nav-icon' });
    }
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="navigation">
        <div className="nav-container">
          <div className="nav-brand" onClick={() => navigate('/dashboard')}>
            <img 
              src="/sheep-logo.png" 
              alt="baad.tv - TV Show Tracker Home"
              className="brand-icon-img"
              width="40"
              height="40"
            />
            <span className="brand-text">Baad.tv</span>
          </div>

          {/* Desktop Navigation Links */}
          <div className="nav-links desktop-only">
            {desktopNavItems.map(item => {
              const isActive = currentPage === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  {renderIcon(item.icon, isActive)}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right side menu items */}
          <div className="user-menu-wrapper">
            {/* Help Dropdown - Mobile Only */}
            <div className="mobile-only">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="help-btn" title="Help">
                    <HelpCircle size={20} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="help-dropdown">
                  <DropdownMenuItem onClick={() => navigate('/help')}>
                    <HelpCircle className="mr-2 h-4 w-4" />
                    <span>Help</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={startTour} data-testid="help-tour-btn">
                    <MapPin className="mr-2 h-4 w-4" />
                    <span>Take a Tour</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/security')}>
                    <Shield className="mr-2 h-4 w-4" />
                    <span>Security & Privacy</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/about')}>
                    <Info className="mr-2 h-4 w-4" />
                    <span>App Info</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Notifications Button */}
            <button
              className="notifications-btn"
              onClick={handleNotificationsToggle}
              title="Notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="user-menu" data-testid="user-menu">
                  <Avatar>
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback>{user.name[0]}</AvatarFallback>
                  </Avatar>
                  <span className="user-name desktop-only">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="user-dropdown">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-btn">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/security')} data-testid="security-btn">
                  <Lock className="mr-2 h-4 w-4" />
                  <span>Security & Privacy</span>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-btn">
                    <Shield className="mr-2 h-4 w-4" />
                    <span>Admin</span>
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin/email')} data-testid="email-admin-btn">
                    <Mail className="mr-2 h-4 w-4" />
                    <span>Email Management</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={startTour} data-testid="tour-btn">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>Take a Tour</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowShareModal(true)} data-testid="share-btn">
                  <Share2 className="mr-2 h-4 w-4" />
                  <span>Share</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <style jsx>{`
          .navigation {
            background: #1a1a1a;
            border-bottom: 1px solid #333333;
            position: sticky;
            top: 0;
            z-index: 50;
          }

          .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 0.75rem 2rem;
            display: flex;
            align-items: center;
            gap: 2rem;
          }

          .nav-brand {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            transition: opacity 0.2s;
          }

          .nav-brand:hover {
            opacity: 0.8;
          }

          .brand-icon-img {
            width: 40px;
            height: 40px;
            object-fit: contain;
          }

          .brand-text {
            font-size: 1.5rem;
            font-weight: 700;
            color: #E62F88;
            font-family: 'Roboto', sans-serif;
          }

          .nav-links {
            display: flex;
            gap: 0.25rem;
            flex: 1;
          }

          .nav-link {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            border: none;
            background: transparent;
            color: #b0b0b0;
            font-size: 0.95rem;
            font-weight: 500;
            cursor: pointer;
            transition: color 0.2s;
          }

          .nav-link:hover {
            color: #ffffff;
          }

          .nav-link.active {
            color: #E62F88;
          }

          .nav-icon {
            width: 18px;
            height: 18px;
          }

          .user-menu-wrapper {
            position: relative;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .help-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: transparent;
            border: none;
            color: #b0b0b0;
            cursor: pointer;
            border-radius: 50%;
            transition: all 0.2s;
          }

          .help-btn:hover {
            background: rgba(230, 47, 136, 0.1);
            color: #E62F88;
          }

          .notifications-btn {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: transparent;
            border: none;
            color: #E62F88;
            cursor: pointer;
            border-radius: 50%;
            transition: all 0.2s;
          }

          .notifications-btn:hover {
            background: rgba(230, 47, 136, 0.1);
            transform: scale(1.05);
          }

          .notification-badge {
            position: absolute;
            top: -2px;
            right: -2px;
            background: #E62F88;
            color: #000000;
            border-radius: 10px;
            padding: 2px 6px;
            font-size: 0.7rem;
            font-weight: 700;
            min-width: 18px;
            text-align: center;
            border: 2px solid #1a1a1a;
          }

          .user-menu {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem 1rem;
            border: none;
            background: transparent;
            cursor: pointer;
            transition: background 0.2s;
            border-radius: 4px;
            outline: none;
            box-shadow: none;
          }

          .user-menu:hover {
            background: #2a2a2a;
          }

          .user-menu:focus {
            outline: none;
            box-shadow: none;
          }

          .user-menu:active {
            outline: none;
            box-shadow: none;
          }

          .user-menu[data-state="open"] {
            background: #2a2a2a;
          }

          .user-menu:focus-visible {
            outline: 2px solid #E62F88;
            outline-offset: 2px;
          }

          .user-name {
            font-weight: 500;
            color: #ffffff;
            font-size: 0.95rem;
          }

          .desktop-only {
            display: flex;
          }

          .mobile-only {
            display: none;
          }

          @media (max-width: 768px) {
            .nav-container {
              padding: 0.75rem 1rem;
              gap: 1rem;
            }

            .nav-brand {
              flex-shrink: 0;
            }

            .brand-icon-img {
              width: 36px;
              height: 36px;
            }

            .brand-text {
              display: none;
            }

            .desktop-only {
              display: none !important;
            }

            .mobile-only {
              display: flex;
            }

            .user-menu {
              padding: 0.25rem 0.5rem;
              gap: 0;
            }

            .user-menu-wrapper {
              flex: 1;
              justify-content: flex-end;
              gap: 0.25rem;
            }

            .notifications-btn {
              width: 36px;
              height: 36px;
            }

            .help-btn {
              width: 36px;
              height: 36px;
            }
          }
        `}</style>

        <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />
        <NotificationsDropdown
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          onMarkAsRead={loadUnreadCount}
        />
      </nav>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav className="bottom-nav">
          {mobileBottomNavItems.map(item => {
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                className={`bottom-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                {renderIcon(item.icon, isActive)}
                <span className="bottom-nav-label">{item.label}</span>
              </button>
            );
          })}

          <style jsx>{`
            .bottom-nav {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              background: #1a1a1a;
              border-top: 1px solid #333333;
              display: flex;
              justify-content: space-around;
              align-items: center;
              padding: 0.5rem 0;
              padding-bottom: calc(0.5rem + env(safe-area-inset-bottom, 0px));
              z-index: 50;
            }

            .bottom-nav-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 0.25rem;
              padding: 0.5rem 1rem;
              border: none;
              background: transparent;
              color: #b0b0b0;
              cursor: pointer;
              transition: all 0.2s;
              min-width: 60px;
            }

            .bottom-nav-item:hover {
              color: #ffffff;
            }

            .bottom-nav-item.active {
              color: #E62F88;
            }

            .bottom-nav-label {
              font-size: 0.7rem;
              font-weight: 500;
              text-align: center;
            }
          `}</style>
        </nav>
      )}
    </>
  );
};

export default Navigation;
